import { box, qs, enc, toPage, type Http, type Page } from './core';

export interface CalendarEvent {
  id: string;
  subject: string | null;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  isAllDay: boolean;
  location?: { displayName?: string };
  attendees?: Array<{ emailAddress: { address: string; name?: string }; type?: string; status?: { response?: string } }>;
  isOnlineMeeting?: boolean;
  onlineMeeting?: { joinUrl?: string } | null;
  webLink?: string;
}
export interface EventInput {
  subject: string;
  /** ISO local `YYYY-MM-DDTHH:mm:ss` theo `timeZone` */
  start: string;
  end: string;
  timeZone?: string;
  attendees?: string[];
  location?: string;
  body?: string;
  /** true → họp Teams (isOnlineMeeting + provider teamsForBusiness) */
  teams?: boolean;
  allDay?: boolean;
}

const TZ = 'SE Asia Standard Time';
const SELECT = 'id,subject,start,end,isAllDay,location,attendees,isOnlineMeeting,onlineMeeting,webLink';

function eventBody(e: Partial<EventInput>, create: boolean) {
  const tz = e.timeZone ?? TZ;
  const b: Record<string, unknown> = {};
  if (e.subject !== undefined) b.subject = e.subject;
  if (e.start !== undefined) b.start = { dateTime: e.start, timeZone: tz };
  if (e.end !== undefined) b.end = { dateTime: e.end, timeZone: tz };
  if (e.allDay !== undefined) b.isAllDay = e.allDay;
  if (e.location !== undefined) b.location = { displayName: e.location };
  if (e.body !== undefined) b.body = { contentType: 'text', content: e.body };
  if (e.attendees) b.attendees = e.attendees.map((address) => ({ emailAddress: { address }, type: 'required' }));
  if (e.teams !== undefined && (create || e.teams)) {
    b.isOnlineMeeting = e.teams;
    if (e.teams) b.onlineMeetingProvider = 'teamsForBusiness';
  }
  return b;
}

export function calendarApi(h: Http) {
  const tzHeader = { Prefer: `outlook.timezone="${TZ}"` };
  return {
    /** Lịch trong khoảng [from, to) (ISO) — dùng calendarView để bung sự kiện lặp. */
    async list(o: { from: string; to: string; mailbox?: string; top?: number; next?: string }): Promise<Page<CalendarEvent>> {
      if (o.next) return toPage(await h.json('calendar', 'GET', o.next, undefined, tzHeader));
      const q = new URLSearchParams({ startDateTime: o.from, endDateTime: o.to, $select: SELECT, $orderby: 'start/dateTime', $top: String(Math.min(o.top ?? 50, 200)) });
      return toPage(await h.json('calendar', 'GET', `${box(o.mailbox)}/calendarView?${qs(q)}`, undefined, tzHeader));
    },
    create: (e: EventInput, mailbox?: string) => h.json<CalendarEvent>('calendar', 'POST', `${box(mailbox)}/events`, eventBody(e, true), tzHeader),
    update: (id: string, e: Partial<EventInput>, mailbox?: string) => h.json<CalendarEvent>('calendar', 'PATCH', `${box(mailbox)}/events/${enc(id)}`, eventBody(e, false), tzHeader),
    async remove(id: string, mailbox?: string) {
      await h.json('calendar', 'DELETE', `${box(mailbox)}/events/${enc(id)}`);
    },
  };
}
